import React, { useRef, useState, useEffect } from "react";
import { Send, Loader2, Sparkles, X } from "lucide-react";
import { XR, xrSession } from "../../lib/xr";

const SUGGESTIONS = [
  "What is happening here?",
  "Who are the Pandavas?",
  "Why is Kurukshetra important?",
  "What should I pay attention to?",
];

export default function GuidePanel({ onClose }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I am your guide here. You stand on the plain of Kurukshetra — the setting of one of the longest-remembered conversations in world literature. Ask me what you'd like to understand before you step forward.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionIdRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || loading) return;
    setMessages((m) => [...m, { role: "user", content: t }]);
    setInput("");
    setLoading(true);
    try {
      const res = await XR.guideChat(t, sessionIdRef.current);
      sessionIdRef.current = res.session_id;
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "The signal is unclear right now. Try asking again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] z-40 flex flex-col mythos-ink-bg border-l border-[#2A3349] shadow-2xl" data-testid="xr-guide-panel">
      <div className="px-5 py-4 border-b border-[#2A3349] flex items-center gap-3">
        <div className="w-10 h-10 rounded-full mythos-gold-bg grid place-items-center text-[#0E0F12] font-mythos font-extrabold">
          K
        </div>
        <div className="flex-1 leading-tight">
          <div className="font-mythos font-extrabold text-[#F4EFE6]">THE GUIDE</div>
          <div className="text-[10px] tracking-[0.18em] uppercase mythos-gold font-bold">Krishna — counsellor</div>
        </div>
        <button onClick={onClose} className="text-[#7d8294] hover:text-[#F4EFE6]" data-testid="xr-guide-close">
          <X size={18} />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 thin-scroll" data-testid="xr-guide-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "mythos-gold-bg text-[#0E0F12] rounded-br-md"
                : "bg-[#0F1623] border border-[#2A3349] text-[#F4EFE6] rounded-bl-md"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-[#7d8294] text-xs">
            <Loader2 className="animate-spin mythos-gold" size={14} /> The Guide is listening…
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-5 pb-3 flex flex-wrap gap-2" data-testid="xr-guide-suggestions">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="text-xs text-[#F4EFE6]/75 border border-[#2A3349] hover:border-[#C9A66B] rounded-full px-3 py-1.5 transition"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="px-5 py-4 border-t border-[#2A3349] bg-[#0E0F12] flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask the Guide…"
          disabled={loading}
          maxLength={600}
          className="flex-1 bg-[#0F1623] border border-[#2A3349] focus:border-[#C9A66B] outline-none rounded-full px-4 py-2.5 text-sm text-[#F4EFE6] placeholder-[#7d8294] disabled:opacity-60"
          data-testid="xr-guide-input"
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || loading}
          className="mythos-gold-bg text-[#0E0F12] rounded-full px-4 font-semibold disabled:opacity-50"
          data-testid="xr-guide-send"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
